import React, { useState, useEffect, useCallback, useRef, useContext } from 'react'
import { Typography, Box, Skeleton } from '@mui/material'
import { NodeRendererProps, Tree, TreeApi } from 'react-arborist'
import { ExpandLess, ExpandMore } from '@mui/icons-material'
import { FaRegUser } from "react-icons/fa"
import { v4 as uuidv4 } from 'uuid'

import { Annotation, FileListNodeProp } from '../../../utils/types'
import { ViewerContext } from './context'
import { handleAnnoCheckbox } from './common'


const Node = ({ node, style }: NodeRendererProps<any>) => {
    return (
        <div style={style} className='node-container cursor-pointer' onClick={() => node.toggle()}>
            {node.data.children
                ? <>
                    {node.isOpen ? <ExpandLess /> : <ExpandMore />}
                    <FaRegUser />
                </>
                : null
            }
            <Typography style={{pointerEvents: 'none'}} className='node-text' title={node.data.name.split('/').pop()}>
                {node.data.name.split('/').pop()}
            </Typography>
        </div>
    )
}

interface UserAnnotationsListProps {
    userMarkers: Annotation[]
    showByUser: Function
    zoomToMarker: Function
    userAnnoLoader: boolean
}

const UserAnnotationsList = ({
    userMarkers,
    showByUser,
    zoomToMarker,
    userAnnoLoader
}: UserAnnotationsListProps) => {
    const { userFilter } = useContext(ViewerContext)
    
    const treeRef = useRef<TreeApi<FileListNodeProp>>(null)
    
    const [boxHeight, setBoxHeight] = useState<number>(0)
    const [nodeList, setNodeList] = useState<FileListNodeProp[]>([])
    const [selectedNode, setSelectedNode] = useState<FileListNodeProp | null>(null)

    const rowHeight = 32
    const maxHeight: number = (window.visualViewport?.height ?? window.innerHeight) - 240

    const createNodeList = useCallback(async () => {
        const catg: {[x: string]: number} = {}
        const temp: {[x: string]: FileListNodeProp[]} = {}

        userMarkers.forEach((i: Annotation) => {
            const user = i.modified_by
            if (user && i.category && i.category.toLowerCase() !== 'negative-area' && !i.is_deleted) {
                catg[i.category] = (catg[i.category] || 0) + 1
                if (!(user.name in temp)) temp[user.name] = []
                temp[user.name].push({
                    id: i.id,
                    name: `${i.category}-${catg[i.category]}`,
                    user_id: user.id
                })
            }
        })
        const nodes = Object.keys(temp).map(key => {
            return {
                id: uuidv4(),
                name: key,
                children: temp[key],
                user_id: temp[key][0].user_id
            }
        })
        setNodeList(nodes)
        setBoxHeight(18 + (nodes.length * rowHeight))
    }, [userMarkers])

    useEffect(() => {
        createNodeList()
    }, [userMarkers])  // eslint-disable-line

    const updateTreeUI = () => {
        try {
            let newHeight = 18
            for (let i = 0; i < nodeList.length; i++) {
                const item = nodeList[i]
                newHeight += rowHeight
                if (userFilter.includes(item.user_id!)) {
                    newHeight += item.children!.length * rowHeight
                    treeRef.current?.open(item.id)
                } else treeRef.current?.close(item.id)
            }
            if (newHeight > maxHeight) setBoxHeight(maxHeight)
            else setBoxHeight(newHeight)
        } catch (err) {
            console.log(err)
        }
    }

    useEffect(() => {
        updateTreeUI()
    }, [userFilter, nodeList])  // eslint-disable-line

    const onSelect = (nodes: any[]) => {
        if (nodes.length) {
            try {
                handleAnnoCheckbox('')
                if (nodes[0].isLeaf) {
                    if (selectedNode && nodes[0].data.id!==selectedNode.id) zoomToMarker(selectedNode.id, true)
                    zoomToMarker(nodes[0].data.id, false)
                } else showByUser(nodes[0].data.user_id)
                setSelectedNode(nodes[0].data)
            } catch (err) {
                console.log(err)
            }
        }
    }

    return (
        <Box sx={{width: '100%', maxHeight: 'calc(100vh - 240px)', overflow: 'auto'}}>
            {userAnnoLoader
                ? <Skeleton variant="rounded" height={34} />
                : nodeList.length
                    ? <Tree
                        ref={treeRef}
                        data={nodeList}
                        openByDefault={false}
                        rowHeight={rowHeight}
                        onSelect={onSelect}
                        height={boxHeight}
                        width='auto'
                        indent={49}
                        disableDrag={true}
                    >
                        {Node}
                    </Tree>
                    : <Typography className='text-secondary'>No user edits</Typography>
            }
        </Box>
    )
}

export default UserAnnotationsList